import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Buildings, Globe, Rocket, Sparkle, MapPin } from "@phosphor-icons/react";
import SectionEyebrow from "@/components/shared/SectionEyebrow";
import useSeo from "@/lib/useSeo";

const CITY = "https://images.pexels.com/photos/4156557/pexels-photo-4156557.jpeg";

const PILLARS = [
  { icon: Rocket, title: "Mission", body: "Modernize Caribbean commerce by connecting customers to vetted local pros across delivery, handyman, cleaning, tech support and events — fast, reliable, on demand." },
  { icon: Globe, title: "Vision", body: "Become the Caribbean's leading multi-service on-demand platform, powering everything from quick deliveries to skilled trades." },
  { icon: Sparkle, title: "Why we exist", body: "Fragmented services, inconsistent quality, and underserved local pros. We're building the trust layer the region deserves." },
];

const FUTURE = [
  "Fintech integration & digital wallet",
  "Regional expansion across the Caribbean",
  "AI-powered dispatch & route optimization",
  "Digital commerce & merchant lending tools",
];

export default function About() {
  useSeo({
    title: "About",
    description: "IslandHop Technologies LLC — a Caribbean technology logistics platform. Our mission, vision and business model.",
  });
  return (
    <div data-testid="about-page">
      <section className="relative pt-20 pb-16 lg:pt-28 lg:pb-24 border-b border-ih-line overflow-hidden">
        <div className="absolute inset-0 ih-grid-bg opacity-40 [mask-image:linear-gradient(180deg,#000,transparent)]" />
        <div className="absolute top-0 right-0 w-[600px] h-[600px] ih-radial-blue" />

        <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="lg:col-span-7 space-y-7">
            <SectionEyebrow label="About IslandHop" color="electric" />
            <h1 className="font-display font-black text-5xl lg:text-7xl text-white leading-[0.95] text-balance">
              A Caribbean<br />
              <span className="text-white/40">technology</span> <span className="text-ih-electric">logistics platform.</span>
            </h1>
            <p className="text-white/70 text-lg lg:text-xl max-w-2xl leading-relaxed">
              IslandHop Technologies LLC is a U.S.-registered technology company building a multi-service
              on-demand platform serving Trinidad &amp; Tobago and the wider Caribbean region — covering delivery,
              handyman, cleaning, tech support and events.
            </p>
          </motion.div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden border border-ih-line2 ih-shadow-blue">
              <img src={CITY} alt="Port of Spain, Trinidad" className="w-full h-[360px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ih-ink to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin weight="fill" size={18} className="text-ih-orange" />
                  <span className="font-display font-bold text-white">Port of Spain</span>
                </div>
                <span className="text-xs font-mono text-white/60">HQ · Trinidad &amp; Tobago</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="grid lg:grid-cols-3 gap-4">
            {PILLARS.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: i * 0.06 }}
                  className="relative bg-ih-surface border border-ih-line rounded-2xl p-8 hover:border-white/20 transition group overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-40 h-40 ih-radial-blue opacity-0 group-hover:opacity-100 transition" />
                  <Icon weight="duotone" size={28} className="text-ih-electric mb-5 relative" />
                  <h3 className="font-display font-bold text-2xl text-white mb-3 relative">{p.title}</h3>
                  <p className="text-white/65 leading-relaxed relative">{p.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Future */}
      <section className="py-24 lg:py-32 border-t border-ih-line">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <SectionEyebrow label="Future vision" color="neon" />
              <h2 className="font-display font-extrabold text-4xl lg:text-6xl text-white leading-[1.02]">
                More than delivery.<br />
                <span className="text-ih-neon">A commerce ecosystem.</span>
              </h2>
              <p className="text-white/65 text-lg leading-relaxed max-w-xl">
                IslandHop is positioned to expand far beyond on-demand delivery — into fintech, regional
                logistics infrastructure and AI-powered commerce.
              </p>
              <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-ih-neon text-black font-semibold hover:scale-[1.02] transition" data-testid="about-contact-cta">
                Talk to the founders
              </Link>
            </div>

            <div className="lg:col-span-6 space-y-3">
              {FUTURE.map((f, i) => (
                <div key={i} className="bg-ih-surface border border-ih-line rounded-2xl p-5 flex items-center gap-4 hover:border-ih-neon/40 transition">
                  <Buildings weight="duotone" size={22} className="text-ih-neon" />
                  <p className="text-white">{f}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
